import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, QueryList, ViewChildren } from '@angular/core';
import { Store } from '@ngrx/store';
import { ReplaySubject, takeUntil } from 'rxjs';

import UserUtil from 'app/core/utilities/user-util';
import { ChatService } from 'app/modules/social/services/chat.service';
import { WebsocketService } from 'app/modules/social/services/websocket.service';
import { socialAppSelectors } from 'app/modules/social/state/app';
import { AppState } from 'app/types';
import { Loggable } from 'app/utils/logging/loggable';
import User from 'entity/User';
import { ChatStatus, OutgoingMessageType } from 'messages/websocket.messages';

import SocialChatMessage from '../../social-chat-message/social-chat-message';
import SocialChatMessageState from '../../social-chat-message/social-chat-message-type';

@Component({
  selector: 'app-moderation-chat-feed',
  templateUrl: './moderation-chat-feed.component.html',
  styleUrls: ['./moderation-chat-feed.component.scss'],
  standalone: false,
})
export class ModerationChatFeedComponent extends Loggable implements AfterViewInit, OnDestroy {
  @ViewChildren('messageBox') private messages?: QueryList<HTMLElement>;

  private destroyed$ = new ReplaySubject<boolean>(1);
  private hostIdList?: string[];

  public constructor(
    public chatService: ChatService,
    private cdr: ChangeDetectorRef,
    private store: Store<AppState>,
    private websocketService: WebsocketService,
  ) {
    super();

    this.store
      .select(socialAppSelectors.getHostIdList)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((hostIdList) => (this.hostIdList = hostIdList));
  }

  public ngAfterViewInit(): void {
    this.scrollToBottom();
    this.cdr.detectChanges();
    this.messages?.changes.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      this.scrollToBottom();
    });
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  private scrollToBottom = (): void => {
    const el = document.getElementById('moderation-chat-feed');

    if (!el) {
      this.error('Unable to get moderation-chat-feed element');
      return;
    }

    el.scrollIntoView({ block: 'end', behavior: 'smooth' });
  };

  public shouldShowError(msg: SocialChatMessage): boolean {
    return msg.messageState === SocialChatMessageState.FAILED;
  }

  public formatUsersName(firstName?: string, lastName?: string) {
    const userFullName = `${firstName || ''} ${lastName || ''}`;
    return UserUtil.trimName(20, userFullName);
  }

  protected isUserHost(user?: User): boolean {
    return this.hostIdList?.find((hostId) => hostId === user?.userId) ? true : false;
  }

  public formatStatusMessage(moderator?: User, status?: ChatStatus): string {
    switch (status) {
      case ChatStatus.APPROVED:
        return `Message Published by ${this.formatUsersName(moderator?.firstName, moderator?.lastName)}`;
      case ChatStatus.REJECTED:
        return `Message Dismissed by ${this.formatUsersName(moderator?.firstName, moderator?.lastName)}`;
      case ChatStatus.REPLIED:
        return `Message Replied by ${this.formatUsersName(moderator?.firstName, moderator?.lastName)}`;
      default:
        return '';
    }
  }

  public formatStatusClass(status?: ChatStatus): string {
    switch (status) {
      case ChatStatus.APPROVED:
      case ChatStatus.REPLIED:
        return 'published';
      case ChatStatus.REJECTED:
        return 'dismissed';
      default:
        return 'pending';
    }
  }

  public isMsgPending(status?: ChatStatus): boolean {
    return status === ChatStatus.PENDING;
  }

  public handleMessageReceived(approved: boolean, msg: SocialChatMessage): void {
    if (!msg) {
      return;
    }

    const objectMessage = {
      t: OutgoingMessageType.CHATMODERATE,
      fromUserId: msg.sender.userId,
      text: msg.message,
      sentDate: msg.sentTimestamp,
      status: approved ? ChatStatus.APPROVED : ChatStatus.REJECTED,
    };

    // Send chat message through web socket message
    this.websocketService.sendMessage(objectMessage);
  }

  public handleReply(msg: SocialChatMessage): void {
    this.chatService.setUserIdToReply(msg.sender.userId, msg);
  }
}
